import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { useAdminApplication, useAdminSession } from "../../app/providers";
import {
  claimTransactionReview,
  decideTransactionReview,
  getTransactionDetail,
  getTransactions,
  requestTransactionSupplement,
} from "./api";
import type { TransactionListQuery } from "./api";
import type { Schemas } from "../workspace/api.generated";

export const transactionKeys = {
  all: (sessionEpoch: number) => ["admin", sessionEpoch, "transactions"] as const,
  lists: (sessionEpoch: number) => [...transactionKeys.all(sessionEpoch), "list"] as const,
  list: (sessionEpoch: number, query: TransactionListQuery) => (
    [...transactionKeys.lists(sessionEpoch), query] as const
  ),
  detail: (sessionEpoch: number, transactionId: number) => (
    [...transactionKeys.all(sessionEpoch), "detail", transactionId] as const
  ),
};

export function useTransactionList(query: TransactionListQuery) {
  const { apiClient } = useAdminApplication();
  const session = useAdminSession();
  return useQuery({
    queryKey: transactionKeys.list(session.sessionEpoch, query),
    queryFn: ({ signal }) => getTransactions(apiClient, query, signal),
    placeholderData: keepPreviousData,
    refetchOnMount: "always",
    staleTime: 0,
  });
}

export function useTransactionDetail(transactionId: number) {
  const { apiClient } = useAdminApplication();
  const session = useAdminSession();
  return useQuery({
    queryKey: transactionKeys.detail(session.sessionEpoch, transactionId),
    queryFn: ({ signal }) => getTransactionDetail(apiClient, transactionId, signal),
    staleTime: 0,
  });
}

function useTransactionMutationInvalidation(transactionId: number) {
  const { queryClient } = useAdminApplication();
  const session = useAdminSession();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: transactionKeys.lists(session.sessionEpoch) }),
      queryClient.invalidateQueries({
        queryKey: transactionKeys.detail(session.sessionEpoch, transactionId),
        refetchType: "none",
      }),
    ]);
  };
}

export function useClaimTransactionReview(transactionId: number) {
  const { apiClient, sessionLifecycle } = useAdminApplication();
  const invalidate = useTransactionMutationInvalidation(transactionId);
  return useMutation({
    mutationFn: async () => {
      const request = sessionLifecycle.createRequestController();
      try {
        return await claimTransactionReview(apiClient, transactionId, request.controller.signal);
      } finally {
        request.release();
      }
    },
    onSuccess: invalidate,
  });
}

export function useDecideTransactionReview(transactionId: number) {
  const { apiClient, sessionLifecycle } = useAdminApplication();
  const invalidate = useTransactionMutationInvalidation(transactionId);
  return useMutation({
    mutationFn: async (input: Schemas["DecideTransactionReviewRequest"]) => {
      const request = sessionLifecycle.createRequestController();
      try {
        return await decideTransactionReview(apiClient, transactionId, input, request.controller.signal);
      } finally {
        request.release();
      }
    },
    onSuccess: invalidate,
  });
}

export function useRequestTransactionSupplement(transactionId: number) {
  const { apiClient, sessionLifecycle } = useAdminApplication();
  const invalidate = useTransactionMutationInvalidation(transactionId);
  return useMutation({
    mutationFn: async (input: Schemas["RequestTransactionSupplementRequest"]) => {
      const request = sessionLifecycle.createRequestController();
      try {
        return await requestTransactionSupplement(apiClient, transactionId, input, request.controller.signal);
      } finally {
        request.release();
      }
    },
    onSuccess: invalidate,
  });
}
